<template>
  <v-container
    fluid
    class="px-8"
    :style="{
      pointerEvents: isReadOnly ? 'none' : 'auto',
    }"
  >
    <v-form ref="form" v-model="valid">
      <v-row>
        <!-- left Section: Profile Upload -->
        <v-col cols="12" md="4" class="d-flex justify-center">
          <ProfileUpload
            v-model="form.photoImage"
            :photo="form.photo"
            :error="photoError"
            @imageUploaded="photoError = !form.photoImage"
            :isReadOnly="isReadOnly"
          />
        </v-col>
        <!-- right Section: Form Fields -->
        <v-col cols="12" md="8">
          <v-row>
            <v-col cols="12" :sm="form.application_status == 'Pending' ? 3 : 4">
              <label class="form-label">DEB ID</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    class="ml-1 pb-1"
                    small
                    :color="getDebColor"
                  >
                    {{ getDebIcon }}
                  </v-icon>
                </template>
                <span>{{ getDebTooltip }}</span>
              </v-tooltip>
              <v-text-field
                v-model="form.deb_id"
                outlined
                dense
                hide-details="auto"
                readonly
              ></v-text-field>
            </v-col>
            <v-col
              v-if="form.application_status == 'Pending'"
              sm="1"
              class="d-flex align-end"
            >
              <v-btn @click="showDebDetails" fab x-small elevation="0"
                ><v-icon small>mdi-pencil</v-icon></v-btn
              >
            </v-col>
            <v-col cols="12" sm="4">
              <label class="form-label"> ABC ID </label>
              <v-text-field
                v-model="form.abc_id"
                outlined
                dense
                hide-details="auto"
                maxlength="12"
                :rules="[rules.number, rules.abcNumber]"
                :readonly="!form.deb_invalid"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="form-label">Programme</label>
              <v-autocomplete
                v-model="form.program_id"
                outlined
                dense
                hide-details="auto"
                :items="programs"
                :item-text="
                  (item) =>
                    `${item.degree.short_name} ${
                      item.specialization ? `- ${item.specialization}` : ''
                    }`
                "
                item-value="id"
                readonly
                append-icon=""
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="form-label">Name (As per latest Certificate)</label>
              <v-text-field
                v-model="form.name"
                outlined
                dense
                hide-details="auto"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="form-label">Name (As per DEB)</label>
              <v-text-field
                v-model="form.deb_map.name"
                outlined
                dense
                hide-details="auto"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="4">
          <label class="form-label">Date of Birth </label>
          <v-text-field
            v-model="formattedDob"
            dense
            outlined
            readonly
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Mobile Number </label>
          <template>
            <div class="d-flex align-center">
              <v-text-field
                v-model="form.mobile_prefix"
                outlined
                dense
                maxlength="5"
                class="mr-2"
                readonly
                style="max-width: 120px"
                hide-details
              ></v-text-field>

              <v-text-field
                v-model="form.mobile_no"
                outlined
                dense
                readonly
                hide-details
              ></v-text-field>
            </div>
          </template>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">WhatsApp Number</label>
          <template>
            <div class="d-flex align-center">
              <v-autocomplete
                v-model="form.whatsapp_mobile_prefix"
                outlined
                dense
                maxlength="5"
                :items="countryCodes"
                class="mr-2"
                style="max-width: 120px"
                hide-details
                attach
              ></v-autocomplete>

              <v-text-field
                v-model="form.whatsapp_mobile_no"
                outlined
                dense
                hide-details
                :rules="[rules.number, rules.mobileNumber]"
                :maxlength="form.mobile_prefix === '+91' ? 10 : 15"
              ></v-text-field>
            </div>
          </template>
        </v-col>

        <v-col cols="12" md="4">
          <label class="form-label"
            >Personal Email Id <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.email"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.required, rules.email]"
            readonly
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label>
            <span class="form-label">Alternate Email Id</span>
            <span class="error--text" v-if="form.deb_invalid">
              - Incase your personal email id is incorrect
            </span>
          </label>
          <v-text-field
            v-model="form.alternate_email"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.email]"
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="4">
          <label class="form-label"
            >Nationality <span class="error--text">* </span></label
          >
          <v-autocomplete
            v-model="form.country_id"
            :items="countries"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.required]"
            :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
            attach
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Religion <span class="error--text">* </span></label
          >
          <v-autocomplete
            v-model="form.religion_id"
            :items="religions"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.required]"
            :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
            attach
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Reservation Category <span class="error--text">* </span></label
          >
          <v-autocomplete
            v-model="form.community_id"
            :items="communities"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.required]"
            :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
            attach
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Caste/Subset</label>
          <v-autocomplete
            v-model="form.caste_id"
            :items="displayedCastes"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details="auto"
            :search-input.sync="searchTerm"
            @keydown.enter="saveNewEntry"
            :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
            attach
          >
            <template v-slot:no-data>
              <v-list-item @click="saveNewEntry">
                <v-list-item-content>
                  <v-list-item-title class="text-primary">
                    Save "{{ searchTerm }}"
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="4">
          <label class="form-label"
            >Gender <span class="error--text">* </span></label
          >
          <v-radio-group
            v-model="form.gender"
            row
            dense
            readonly
            :rules="[rules.required]"
            hide-details="auto"
            class="pt-0"
          >
            <v-radio label="Male" value="Male"></v-radio>
            <v-radio label="Female" value="Female"></v-radio>
            <v-radio label="Other" value="Other"></v-radio>
          </v-radio-group>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Are you serving in Air Force </label>
          <v-radio-group v-model="form.indian_air_force" row dense class="pt-0">
            <v-radio label="Yes" value="true"></v-radio>
            <v-radio label="No" value="false"></v-radio>
          </v-radio-group>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Resident Status </label>
          <v-radio-group
            v-model="form.residence"
            row
            dense
            :rules="[rules.required]"
            hide-details="auto"
            class="pt-0 text-arrange"
          >
            <v-radio label="Resident Indian" value="true">
              <template v-slot:label>
                <span class="custom-label">Resident Indian</span>
              </template></v-radio
            >
            <v-radio
              class="text-arrange"
              label="Non Resident Indian"
              value="false"
              ><template v-slot:label>
                <span class="custom-label">Non Resident Indian</span>
              </template></v-radio
            >
          </v-radio-group>
        </v-col>
        <v-col cols="12" md="4" v-if="form.residence === 'false'">
          <label class="form-label"
            >Passport Number <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.passport_number"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.required, rules.passportNumberRule]"
            @change="form.passport_number = form.passport_number.toUpperCase()"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4" v-if="form.residence === 'false'">
          <label class="form-label"
            >Date of Issue <span class="error--text">* </span></label
          >
          <v-menu
            v-model="menu1"
            :close-on-content-click="false"
            transition="scale-transition"
            max-width="290"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedPassportDate"
                dense
                outlined
                readonly
                placeholder="dd-mm-yyyy"
                :rules="[rules.required]"
                v-bind="attrs"
                hide-details="auto"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="form.passport_date_of_issue"
              @input="menu1 = false"
              no-title
              :max="today"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="4" v-if="form.residence === 'false'">
          <label class="form-label"
            >Place of issue <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.passport_place_of_issue"
            outlined
            dense
            :rules="[rules.required]"
            hide-details="auto"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4" v-if="form.residence != 'false'">
          <label class="form-label"
            >Aadhaar Number <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.aadhar_card_no"
            outlined
            dense
            maxlength="12"
            hide-details="auto"
            :rules="[rules.required, rules.aadhaarNumber]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4" v-if="form.residence != 'false'">
          <label class="form-label inputFile">
            Aadhar Card Upload <span class="error--text">*</span>
          </label>
          <v-text-field
            v-if="form.is_aadhar_attached"
            v-model="form.aadhar.file_name"
            class=""
            hint="Aadhar attached"
            persistent-hint
            dense
            readonly
            outlined
            @click="$refs.aadharInput.click()"
            prepend-inner-icon="mdi-file-upload"
          ></v-text-field>
          <v-file-input
            v-else
            v-model="form.aadharImage"
            outlined
            dense
            accept="application/pdf, image/png, image/jpeg, image/jpg"
            hide-details="auto"
            :rules="[rules.required]"
            prepend-inner-icon="mdi-file-upload"
            prepend-icon=""
          ></v-file-input>
          <input
            type="file"
            style="display: none"
            ref="aadharInput"
            @change="onFileChanged($event, 'aadharImage', 'is_aadhar_attached')"
            accept="application/pdf, image/png, image/jpeg, image/jpg"
          />
        </v-col>
        <v-col cols="12" md="4"></v-col>
        <v-col cols="12" md="4" v-if="form.residence != 'false'">
          <label class="form-label">PAN Number</label>
          <v-text-field
            v-model="form.pan_card_no"
            outlined
            dense
            hide-details="auto"
            maxlength="10"
            :rules="[rules.panNumber]"
            @input="form.pan_card_no = form.pan_card_no.toUpperCase()"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4" v-if="form.residence != 'false'">
          <label class="form-label">PAN Card Upload </label>
          <v-text-field
            v-if="form.is_pan_attached"
            v-model="form.pan.file_name"
            class=""
            hint="Pan attached"
            persistent-hint
            dense
            readonly
            outlined
            @click="$refs.panInput.click()"
            prepend-inner-icon="mdi-file-upload"
          ></v-text-field>
          <v-file-input
            v-else
            v-model="form.panImage"
            outlined
            dense
            accept="application/pdf, image/png, image/jpeg, image/jpg"
            hide-details="auto"
            prepend-inner-icon="mdi-file-upload"
            prepend-icon=""
          ></v-file-input>
          <input
            type="file"
            style="display: none"
            ref="panInput"
            @change="onFileChanged($event, 'panImage', 'is_pan_attached')"
            accept="application/pdf, image/png, image/jpeg, image/jpg"
          />
        </v-col>
      </v-row>
      <v-card elevation="0" class="d-flex justify-between">
        <v-card-title class="flex pl-0">Parent/Spouse/Guardian</v-card-title>
      </v-card>
      <v-row>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Relation <span class="error--text">* </span></label
          >
          <v-autocomplete
            v-model="form.family.relationship"
            :items="relations"
            item-text="name"
            item-value="id"
            outlined
            dense
            :rules="[rules.required]"
            required
            hide-details="auto"
            :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
            attach
            menu-props="auto"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4" v-if="form.family.relationship === 'Other'">
          <label class="form-label"
            >Specify the relation <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.family.relation_type"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.required]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Name <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.family.name"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.required, rules.onlyString]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Mobile Number <span class="error--text">* </span></label
          >
          <template>
            <div class="d-flex align-center justify-center">
              <v-autocomplete
                v-model="form.family.mobile_prefix"
                outlined
                dense
                maxlength="5"
                :items="countryCodes"
                class="mr-2"
                style="max-width: 120px"
                :rules="[rules.required]"
                hide-details
                attach
                :menu-props="{ top: true, offsetY: true }"
              ></v-autocomplete>

              <v-text-field
                v-model="form.family.mobile_no"
                outlined
                dense
                :rules="[rules.required, rules.number, rules.mobileNumber]"
                :maxlength="form.family.mobile_prefix === '+91' ? 10 : 15"
                required
                hide-details
              ></v-text-field>
            </div>
          </template>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Email Id </label>
          <v-text-field
            v-model="form.family.email"
            outlined
            dense
            :rules="[rules.email]"
            hide-details="auto"
          ></v-text-field>
        </v-col>
      </v-row>
      <!-- Buttons -->
      <v-row v-if="!isReadOnly" class="form-row mt-6">
        <v-col cols="12" class="d-flex justify-end pr-0">
          <v-btn color="error" class="mr-4" @click="confirmReset">
            Reset
          </v-btn>
          <v-btn
            color="success"
            class="mr-3"
            :disabled="
              JSON.stringify(this.value) === JSON.stringify(this.form) ||
              submitLoading
            "
            :loading="submitLoading"
            @click="submit"
          >
            Save & Next
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-dialog v-model="showDebDialog" width="450" persistent>
      <v-card>
        <v-form ref="formDeb" v-model="debValid">
          <v-card-title class="text-caption"
            >Enter DEB ID for validation</v-card-title
          >
          <v-card-text class="pb-0">
            <v-text-field
              v-model="updated_deb"
              hide-details="auto"
              outlined
              :rules="[rules.required, rules.number, rules.debId]"
              maxlength="12"
              dense
            ></v-text-field>
          </v-card-text>
          <v-card-actions class="py-3">
            <v-spacer></v-spacer>
            <v-btn
              small
              color="error"
              class="mr-2"
              @click="showDebDialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              :disabled="!debValid"
              small
              color="success"
              class="mr-2"
              :loading="loading"
              @click="debValidate()"
            >
              Proceed
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmationDialog" width="450" persistent>
      <confirmationDialogForm
        v-if="confirmationDialog"
        :confirmationText="confirmationText"
        @closeDialog="confirmationDialog = false"
        @saveDialog="reset"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import ProfileUpload from "./ProfileUpload.vue";
import ImageUpload from "./ImageUpload.vue";
import { cloneDeep } from "lodash";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import confirmationDialogForm from "../components/confirmationDialog.vue";

export default {
  components: { ProfileUpload, ImageUpload, confirmationDialogForm },
  props: {
    value: {
      type: Object,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    submitLoading: { type: Boolean, default: false },
  },
  data: () => ({
    rules,
    menu: false,
    menu1: false,
    valid: false,
    today: new Date().toISOString().substr(0, 10),
    relations: ["Father", "Mother", "Spouse", "Guardian", "Other"],
    castes: [],
    communities: [],
    programs: [],
    religions: [],
    countries: [],
    searchTerm: "",
    photoError: false,
    isImageUploaded: false,
    showImageError: false,
    form: {},
    countryCodes: [],
    confirmationDialog: false,
    confirmationText: "",
    showDebDialog: false,
    loading: false,
    updated_deb: "",
    debValid: false,
  }),
  watch: {
    value: {
      handler(newVal) {
        this.form = cloneDeep(newVal);
      },
      deep: true,
    },
    valid(newValue) {
      if (
        !newValue ||
        !(this.form.photoImage || this.form.photo) ||
        JSON.stringify(this.value) !== JSON.stringify(this.form)
      ) {
        this.$emit("validate", false);
      } else {
        this.$emit("validate", true);
      }
    },
    form: {
      handler(newVal) {
        if (this.valid && (newVal.photoImage || newVal.photo)) {
          if (JSON.stringify(this.value) !== JSON.stringify(newVal)) {
            this.$emit("validate", false);

            // this.$emit("input", { ...this.form });
          } else {
            this.$emit("validate", true);
          }
        } else {
          this.$emit("validate", false);
        }
      },
      deep: true,
    },
  },
  computed: {
    formattedDob() {
      if (!this.form.dob) return "";
      const [year, month, day] = this.form.dob.split("-");
      return `${day}-${month}-${year}`;
    },
    formattedPassportDate() {
      if (!this.form.passport_date_of_issue) return "";
      const [year, month, day] = this.form.passport_date_of_issue.split("-");
      return `${day}-${month}-${year}`;
    },
    displayedCastes() {
      if (!this.searchTerm) return this.castes;
      return this.castes.filter((c) =>
        c.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
      );
    },
    getDebIcon() {
      if (!this.form.deb_validated) {
        return "mdi-alert-circle-outline";
      } else if (this.form.deb_invalid) {
        return "mdi-close-circle";
      } else {
        return "mdi-check-circle";
      }
    },
    getDebColor() {
      if (!this.form.deb_validated) {
        return "orange";
      } else if (this.form.deb_invalid) {
        return "red";
      } else {
        return "green";
      }
    },
    getDebTooltip() {
      if (!this.form.deb_validated) {
        return "DEB ID could not be validated";
      } else if (this.form.deb_invalid) {
        return "Invalid DEB ID";
      } else {
        return "DEB ID validated";
      }
    },
  },
  created() {
    this.form = cloneDeep(this.value);
    this.init();
  },
  methods: {
    onFileChanged(file, value, boolean) {
      this.form[value] = file.target.files[0];
      this.form[boolean] = false;
    },
    async init() {
      await Promise.all([
        this.getReligions(),
        this.getCastes(),
        this.getCommunities(),
        this.getCountries(),
        this.getPrograms(),
        this.getCountryCodes(),
      ]);
      this.$set(this.form.family, "mobile_prefix", "+91");
    },
    getCountryCodes() {
      const codes = getCountries().map(
        (countryCode) => "+" + getCountryCallingCode(countryCode),
      );
      this.countryCodes = [...new Set(codes)].sort((a, b) => {
        return parseInt(a.slice(1)) - parseInt(b.slice(1));
      });
    },
    async saveCaste() {
      if (!this.searchTerm) return;
      const caste = this.castes.find((c) => c.id === this.form.caste_id);
      if (caste.isNew) {
        try {
          const response = await this.$axios.post("/castes", { caste: caste });
          console.log(response);
        } catch (error) {
          console.log(error);
        }
      }
    },
    saveNewEntry() {
      const newCaste = {
        name: this.searchTerm,
        id: this.castes.length + 1,
        isNew: true,
      };
      this.castes.push(newCaste);
      this.form.caste_id = newCaste.id;
    },
    async getPrograms() {
      try {
        const response = await this.$axios.get("/programs");
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getReligions() {
      try {
        const response = await this.$axios.get("/religions");
        this.religions = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getCastes() {
      try {
        const response = await this.$axios.get("/castes");
        this.castes = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getCommunities() {
      try {
        const response = await this.$axios.get("/communities");
        this.communities = response.data.data.sort((a, b) => a.id - b.id);
      } catch (error) {
        console.log(error);
      }
    },
    async getCountries() {
      try {
        const response = await this.$axios.get("/countries");
        this.countries = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    customFilter(item, queryText) {
      if (queryText.length < 3) return false;
      return item.name.toLowerCase().includes(queryText.toLowerCase());
    },
    async submit() {
      this.photoError = !this.form.photoImage;
      let checkImage;
      if (
        this.form.application_status === "Pending" &&
        this.form.photoImage == null
      ) {
        checkImage = false;
      }
      if (
        this.form.application_status === "Draft" &&
        this.form.photoImage == null
      ) {
        checkImage = true;
      }
      if (
        !this.$refs.form.validate() ||
        checkImage ||
        (this.photoError && !this.form.photo)
      ) {
        window.scrollTo(0, 0);
        return;
      }
      await this.saveCaste();
      this.$emit("validate", true);
      this.$emit("submit", this.form);
    },
    showError() {
      return this.showImageError; // Directly return the flag
    },
    confirmReset() {
      this.confirmationText =
        "This will clear the data you have entered in this page. Are you sure you want to reset ?";
      this.confirmationDialog = true;
    },
    reset() {
      this.form = {
        ...this.form,
        photo: null,
        photoImage: null,
        whatsapp_mobile_no: null,
        alternate_email: null,
        country_id: null,
        religion_id: null,
        caste_id: null,
        community_id: null,
        gender: null,
        passport_number: null,
        passport_date_of_issue: null,
        passport_place_of_issue: null,
        aadhar_card_no: null,
        aadharImage: null,
        aadhar: null,
        pan_card_no: null,
        panImage: null,
        pan: null,
        is_aadhar_attached: false,
        is_pan_attached: false,
        family: {
          relationship: null,
          name: null,
          relation_type: null,
          mobile_no: null,
          mobile_prefix: null,
          email: null,
        },
      };
      this.$refs.form.resetValidation();
      this.confirmationDialog = false;
    },

    handleImageUpload(file) {
      this.isImageUploaded = !!file;
      if (file) {
        this.showImageError = false;
      }
    },
    validateForm() {
      this.showImageError = !this.isImageUploaded;
      return this.isImageUploaded && this.aadhaarNumber;
    },
    debValidate() {
      if (!this.$refs.formDeb.validate()) return;
      this.form.deb_id = this.updated_deb;
      this.loading = true;
      this.$axios
        .get(`/validate_deb_id?id=${this.form.id}&deb_id=${this.form.deb_id}`)
        .then((res) => {
          console.log(res);
          this.loading = false;
          this.showDebDialog = false;
          // window.location.reload()
        })
        .catch(() => {
          this.loading = false;
          this.showDebDialog = false;
        });
    },
    showDebDetails() {
      this.updated_deb = this.form.deb_id;
      this.showDebDialog = true;
    },
  },
};
</script>

<style scoped>
.input-col {
  padding-top: 0; /* Ensure inputs align properly */
}

.form-label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.8);
}

.custom-label {
  font-size: 14px;
  line-height: 1rem;
}
::v-deep(.v-messages) {
  color: green !important;
}
</style>
